import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ToolId, TextDefaults, ShapeDefaults, LineDefaults } from '@/types/tools';
import { solid, type Brush } from '@/types/brush';

interface ToolbarState {
  activeTool: ToolId;
  orientation: 'vertical' | 'horizontal';
  position: { x: number; y: number };

  // Text tool defaults
  textDefaults: TextDefaults;

  // Shape tool defaults
  shapeDefaults: ShapeDefaults;

  // Line tool defaults
  lineDefaults: LineDefaults;
}

interface ToolbarActions {
  setActiveTool: (tool: ToolId) => void;
  setOrientation: (orientation: 'vertical' | 'horizontal') => void;
  toggleOrientation: () => void;
  setPosition: (position: { x: number; y: number }) => void;
  updateTextDefaults: (updates: Partial<TextDefaults>) => void;
  updateShapeDefaults: (updates: Partial<ShapeDefaults>) => void;
  updateLineDefaults: (updates: Partial<LineDefaults>) => void;
  resetToSelect: () => void;
}

const initialState: ToolbarState = {
  activeTool: 'select',
  orientation: 'vertical',
  position: { x: 16, y: 16 },

  textDefaults: {
    fontSize: 24,
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    color: solid('#FFFFFF'),
    textAlign: 'center',
  },

  shapeDefaults: {
    shapeType: 'rectangle',
    fill: solid('#FFFFFF'),
    stroke: null,
    strokeWidth: 0,
    borderRadius: 8,
  },

  lineDefaults: {
    stroke: solid('#FFFFFF'),
    strokeWidth: 2,
  },
};

// Helper to migrate old string-based colors to Brush type
function migrateBrush(value: string | Brush | null | undefined): Brush | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    if (value === '' || value === 'none' || value === 'transparent') return null;
    return solid(value);
  }
  return value;
}

function migrateState(state: any): ToolbarState {
  return {
    ...state,
    textDefaults: {
      ...state.textDefaults,
      color: migrateBrush(state.textDefaults?.color) ?? solid('#FFFFFF'),
    },
    shapeDefaults: {
      ...state.shapeDefaults,
      fill: migrateBrush(state.shapeDefaults?.fill) ?? solid('#FFFFFF'),
      stroke: migrateBrush(state.shapeDefaults?.stroke),
    },
    lineDefaults: {
      ...state.lineDefaults,
      stroke: migrateBrush(state.lineDefaults?.stroke) ?? solid('#FFFFFF'),
    },
  };
}

export const useToolbarStore = create<ToolbarState & ToolbarActions>()(
  persist(
    (set) => ({
      ...initialState,

      setActiveTool: (tool) => set({ activeTool: tool }),

      setOrientation: (orientation) => set({ orientation }),

      toggleOrientation: () =>
        set((state) => ({
          orientation: state.orientation === 'vertical' ? 'horizontal' : 'vertical',
        })),

      setPosition: (position) => set({ position }),

      updateTextDefaults: (updates) =>
        set((state) => ({
          textDefaults: { ...state.textDefaults, ...updates },
        })),

      updateShapeDefaults: (updates) =>
        set((state) => ({
          shapeDefaults: { ...state.shapeDefaults, ...updates },
        })),

      updateLineDefaults: (updates) =>
        set((state) => ({
          lineDefaults: { ...state.lineDefaults, ...updates },
        })),

      resetToSelect: () => set({ activeTool: 'select' }),
    }),
    {
      name: 'adstudio-toolbar',
      version: 1, // Bump version to trigger migration
      partialize: (state) => ({
        orientation: state.orientation,
        position: state.position,
        textDefaults: state.textDefaults,
        shapeDefaults: state.shapeDefaults,
        lineDefaults: state.lineDefaults,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version === 0 || !version) {
          // Migrate from string colors to Brush types
          return migrateState(persistedState);
        }
        return persistedState as ToolbarState;
      },
    }
  )
);

// Selector hooks
export function useActiveTool() {
  return useToolbarStore((s) => s.activeTool);
}

export function useToolbarOrientation() {
  return useToolbarStore((s) => s.orientation);
}

export function useToolbarPosition() {
  return useToolbarStore((s) => s.position);
}
